/**
 * Tenant config **change history** card.
 *
 * A configurable-ready content component: it renders the integrated `DataList`
 * (server mode → `GET /config/history`) from a declarative, stable-keyed column
 * config, reads the injected `_layoutOverrides` meta-prop, publishes its base
 * config for the admin layout editor, and resolves its title through
 * `resolveCardTitle`. Registered in `../register.ts` as
 * `claimflow.configHistoryCard`.
 *
 * Each row exposes two actions:
 *  - **Détails** → read-only inspect modal (`GET /config/versions/{id}` diff).
 *  - **Restaurer** → two-step revert: a dry-run (`dry_run: true`) diff preview,
 *    then on confirm the real revert (`dry_run: false`) + success toast.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { History } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { Alert, Modal, Spinner, useToast } from '@components/base/feedback';
import { HStack, VStack } from '@components/base/layout';
import {
  DataList,
  dataListWorkflowRegistry,
  useDataList,
} from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import {
  entityTypeLabel,
  type ConfigVersion,
} from '../../api/configVersioningService';
import {
  useConfigVersion,
  useRevertVersion,
} from '../../hooks/configVersioning';
import { createConfigHistoryListConfig } from '../../configs/configVersioningListConfigs';
import { VersionDiffView } from './VersionDiffView';
import { restoreReportSummary } from './configVersioningFormat';

const COMPONENT_ID = 'claimflow.configHistoryCard';
const BASE_TITLE = 'Historique des modifications';

export interface ConfigHistoryCardProps {
  /**
   * Per-tenant column layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the column config renders byte-identically.
   */
  _layoutOverrides?: LayoutOverrides;
}

export function ConfigHistoryCard({
  _layoutOverrides,
}: Readonly<ConfigHistoryCardProps> = {}) {
  const { toast } = useToast();

  const baseConfig = useMemo(() => createConfigHistoryListConfig(), []);
  usePublishLayoutConfig(
    COMPONENT_ID,
    buildPublishedConfigFromDataList(baseConfig, BASE_TITLE),
  );
  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );
  const list = useDataList(config);

  const [detailsTarget, setDetailsTarget] = useState<ConfigVersion | null>(null);
  const [revertTarget, setRevertTarget] = useState<ConfigVersion | null>(null);

  // Register the row-action handlers so the config's `handler` keys resolve to
  // component state setters (mirrors TaskListCard's rowClick registration).
  useEffect(() => {
    dataListWorkflowRegistry.registerRowActionHandler(
      'claimflow-config-details',
      (row) => {
        setDetailsTarget(row as unknown as ConfigVersion);
        return Promise.resolve();
      },
    );
    dataListWorkflowRegistry.registerRowActionHandler(
      'claimflow-config-revert',
      (row) => {
        setRevertTarget(row as unknown as ConfigVersion);
        return Promise.resolve();
      },
    );
  }, []);

  const closeDetails = useCallback(() => {
    setDetailsTarget(null);
  }, []);
  const closeRevert = useCallback(() => {
    setRevertTarget(null);
  }, []);

  const handleRevertApplied = useCallback(
    (summary: string) => {
      toast({
        variant: 'success',
        title: 'Restauration appliquée',
        message: summary,
      });
      setRevertTarget(null);
    },
    [toast],
  );

  return (
    <div>
      <div className="flex items-center gap-2 bg-secondary-50 border-b border-secondary-200 px-4 py-3 rounded-t-lg">
        <Icon icon={History} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle(BASE_TITLE, _layoutOverrides)}
        </Text>
        <Text as="span" size="sm" color="muted">
          ({list.totalItems})
        </Text>
      </div>

      <DataList config={config} list={list}>
        <DataList.Toolbar />
        <DataList.Table />
        <DataList.Footer />
      </DataList>

      <ConfigVersionDetailsModal
        version={detailsTarget}
        onClose={closeDetails}
      />
      <ConfigRevertModal
        version={revertTarget}
        onClose={closeRevert}
        onApplied={handleRevertApplied}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details (inspect) modal
// ---------------------------------------------------------------------------

function ConfigVersionDetailsModal({
  version,
  onClose,
}: Readonly<{ version: ConfigVersion | null; onClose: () => void }>) {
  const { data, isLoading, isError, error } = useConfigVersion(
    version?.id ?? null,
  );

  return (
    <Modal open={version !== null} onClose={onClose} size="lg">
      <Modal.Header title="Détails de la version" />
      <Modal.Body>
        {version && (
          <VStack gap={3}>
            <Text size="sm" color="muted">
              {entityTypeLabel(version.entity_type)} · {version.entity_key} —
              version n°{version.version}
            </Text>
            {isLoading && (
              <HStack gap={2} align="center">
                <Spinner size="sm" />
                <Text size="sm" color="muted">
                  Chargement du détail…
                </Text>
              </HStack>
            )}
            {isError && (
              <Alert variant="error" title="Échec du chargement">
                {error instanceof Error
                  ? error.message
                  : 'Impossible de charger le détail de la version.'}
              </Alert>
            )}
            {data && <VersionDiffView diff={data.diff} />}
          </VStack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button variant="secondary" onClick={onClose}>
            Fermer
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Revert (dry-run preview → confirm) modal
// ---------------------------------------------------------------------------

function ConfigRevertModal({
  version,
  onClose,
  onApplied,
}: Readonly<{
  version: ConfigVersion | null;
  onClose: () => void;
  onApplied: (summary: string) => void;
}>) {
  const dryRun = useRevertVersion();
  const apply = useRevertVersion();

  const { mutate: mutateDryRun, reset: resetDryRun } = dryRun;
  const { reset: resetApply } = apply;

  // Fire the dry-run preview whenever a new version is selected for revert.
  useEffect(() => {
    if (!version) {
      resetDryRun();
      resetApply();
      return;
    }
    mutateDryRun({ versionId: version.id, dryRun: true });
  }, [version, mutateDryRun, resetDryRun, resetApply]);

  const handleConfirm = useCallback(() => {
    if (!version) return;
    apply.mutate(
      { versionId: version.id, dryRun: false },
      {
        onSuccess: (result) => {
          onApplied(
            result.report
              ? restoreReportSummary(result.report)
              : 'Restauration effectuée.',
          );
        },
      },
    );
  }, [apply, version, onApplied]);

  return (
    <Modal open={version !== null} onClose={onClose} size="lg">
      <Modal.Header title="Restaurer cette version" />
      <Modal.Body>
        {version && (
          <VStack gap={3}>
            <Text size="sm">
              Vous êtes sur le point de restaurer{' '}
              <Text as="span" weight="bold">
                {entityTypeLabel(version.entity_type)} · {version.entity_key}
              </Text>{' '}
              à sa version n°{version.version}. Aperçu des changements :
            </Text>

            {dryRun.isPending && (
              <HStack gap={2} align="center">
                <Spinner size="sm" />
                <Text size="sm" color="muted">
                  Calcul de l’aperçu…
                </Text>
              </HStack>
            )}
            {dryRun.isError && (
              <Alert variant="error" title="Échec de l’aperçu">
                {dryRun.error instanceof Error
                  ? dryRun.error.message
                  : 'Impossible de calculer l’aperçu de la restauration.'}
              </Alert>
            )}
            {dryRun.data && <VersionDiffView diff={dryRun.data.diff} />}

            {apply.isError && (
              <Alert variant="error" title="Échec de la restauration">
                {apply.error instanceof Error
                  ? apply.error.message
                  : 'La restauration a échoué.'}
              </Alert>
            )}
          </VStack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={apply.isPending}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={apply.isPending}
            disabled={dryRun.isPending || dryRun.isError}
            onClick={handleConfirm}
          >
            Confirmer la restauration
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}
